import BasicSection from "components/BasicSection";
import Layout from "components/Layout";
import { PageProps } from "gatsby";
import React, { useState } from "react";
import {
    ArrowsPointingInIcon,
    CurrencyDollarIcon,
    LockClosedIcon,
    ShieldCheckIcon,
    BookOpenIcon,
    CloudArrowUpIcon,
    HeartIcon,
    LinkIcon,
    ArrowsUpDownIcon,
    FolderArrowDownIcon,
    ChatBubbleBottomCenterIcon,
    MagnifyingGlassIcon,
    EyeSlashIcon,
    QuestionMarkCircleIcon,
    UserGroupIcon,
    EllipsisHorizontalCircleIcon,
    SunIcon,
    CakeIcon,
    FingerPrintIcon,
    BoltIcon,
} from "@heroicons/react/24/outline";
import styled from "styled-components";
import { Col, Container, Row } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import i18n from "../../i18n";

interface PageContextProps {
    language: string;
}

const FeaturesPage: React.FC<PageProps<unknown, PageContextProps>> = ({
    path,
    pageContext,
}) => {
    const { language } = pageContext;

    if (i18n.language !== language) {
        i18n.changeLanguage(language);
    }

    const { t } = useTranslation();

    const downloadURL =
        i18n.language === "en" ? "/download" : `/${i18n.language}/download`;

    const features = [
        {
            titleKey: "EP_FEATURES_E2EE_TITLE",
            descriptionKey: "EP_FEATURES_E2EE_DESCRIPTION",
            slug: "lock",
            components: { 1: <a href="/architecture" /> },
        },
        {
            titleKey: "EP_FEATURES_3_REPLICAS_TITLE",
            descriptionKey: "EP_FEATURES_3_REPLICAS_DESCRIPTION",
            slug: "shield",
            components: { 1: <a href="/reliability" /> },
        },
        {
            titleKey: "EP_FEATURES_CROSS_PLATFORM_TITLE",
            descriptionKey: "EP_FEATURES_CROSS_PLATFORM_DESCRIPTION",
            slug: "open",
            components: { 1: <a href="https://github.com/ente-io/" /> },
        },
        {
            titleKey: "EP_FEATURES_BACKGROUND_UPLOADS_TITLE",
            descriptionKey: "EP_FEATURES_BACKGROUND_UPLOADS_DESCRIPTION",
            slug: "upload",
        },
        {
            titleKey: "EP_FEATURES_FAMILY_PLANS_TITLE",
            descriptionKey: "EP_FEATURES_FAMILY_PLANS_DESCRIPTION",
            slug: "money",
        },
        {
            titleKey: "EP_FEATURES_SEARCH_TITLE",
            descriptionKey: "EP_FEATURES_SEARCH_DESCRIPTION",
            slug: "search",
        },
        {
            titleKey: "EP_FEATURES_ALBUM_SHARING_TITLE",
            descriptionKey: "EP_FEATURES_ALBUM_SHARING_DESCRIPTION",
            slug: "share",
        },
        {
            titleKey: "EP_FEATURES_LINK_SHARING_TITLE",
            descriptionKey: "EP_FEATURES_LINK_SHARING_DESCRIPTION",
            slug: "link",
            components: {
                1: (
                    <a href="https://albums.ente.io/?t=CsuRcc4k#37fb731a4915aff5adba2cb25d9f4aa2ea13e1e5952ec27233dce5033c28af7a" />
                ),
            },
        },
        {
            titleKey: "EP_FEATURES_COLLABORATION_TITLE",
            descriptionKey: "EP_FEATURES_COLLABORATION_DESCRIPTION",
            slug: "collaborate",
        },
        {
            titleKey: "EP_FEATURES_EASY_IMPORT_TITLE",
            descriptionKey: "EP_FEATURES_EASY_IMPORT_DESCRIPTION",
            slug: "import",
            components: { 1: <a href={downloadURL} /> },
        },
        {
            titleKey: "EP_FEATURES_EASY_EXPORT_TITLE",
            descriptionKey: "EP_FEATURES_EASY_EXPORT_DESCRIPTION",
            slug: "export",
            components: { 1: <a href={downloadURL} /> },
        },
        {
            titleKey: "EP_FEATURES_MEMORIES_TITLE",
            descriptionKey: "EP_FEATURES_MEMORIES_DESCRIPTION",
            slug: "memories",
        },
        {
            titleKey: "EP_FEATURES_HIDDEN_PHOTOS_TITLE",
            descriptionKey: "EP_FEATURES_HIDDEN_PHOTOS_DESCRIPTION",
            slug: "hidden",
        },
        {
            titleKey: "EP_FEATURES_DESCRIPTIONS_TITLE",
            descriptionKey: "EP_FEATURES_DESCRIPTIONS_DESCRIPTION",
            slug: "description",
        },
        {
            titleKey: "EP_FEATURES_ADDITIONAL_SECURITY_TITLE",
            descriptionKey: "EP_FEATURES_ADDITIONAL_SECURITY_DESCRIPTION",
            slug: "2fa",
        },
        {
            titleKey: "EP_FEATURES_FREE_DEVICE_SPACE_TITLE",
            descriptionKey: "EP_FEATURES_FREE_DEVICE_SPACE_DESCRIPTION",
            slug: "free",
        },
        {
            titleKey: "EP_FEATURES_DARK_LIGHT_THEMES_TITLE",
            descriptionKey: "EP_FEATURES_DARK_LIGHT_THEMES_DESCRIPTION",
            slug: "theme",
        },
        {
            titleKey: "EP_FEATURES_SUPPORT_TITLE",
            descriptionKey: "EP_FEATURES_SUPPORT_DESCRIPTION",
            slug: "support",
            components: { 1: <a href="mailto:support@ente.io" /> },
        },
        {
            titleKey: "EP_FEATURES_COMMUNITY_TITLE",
            descriptionKey: "EP_FEATURES_COMMUNITY_DESCRIPTION",
            slug: "community",
            components: {
                1: (
                    <a
                        href={
                            i18n.language === "en"
                                ? "/about#community"
                                : `/${i18n.language}/about#community`
                        }
                    />
                ),
            },
        },
        {
            titleKey: "EP_FEATURES_AND_MORE_TITLE",
            descriptionKey: "EP_FEATURES_AND_MORE_DESCRIPTION",
            slug: "more",
            components: {
                1: (
                    <a href="https://github.com/ente-io/ente/discussions/categories/enhancements" />
                ),
            },
        },
    ];

    return (
        <Layout
            title={t("EP_FEATURES_SEO_TITLE")}
            description={t("EP_FEATURES_SEO_DESCRIPTION")}
            path={path}
            localised={true}
            language={pageContext.language}
        >
            <BasicSection>
                <BasicSection.Header>
                    <BasicSection.Heading>
                        {t("EP_FEATURES_TITLE")}
                    </BasicSection.Heading>
                </BasicSection.Header>

                <BasicSection.BodyElevated>
                    <Container>
                        <Row xs={1} md={2}>
                            {features.map((feature) => (
                                <FeatureCard
                                    key={feature.slug}
                                    titleKey={feature.titleKey}
                                    descriptionKey={feature.descriptionKey}
                                    components={feature.components}
                                    slug={feature.slug}
                                />
                            ))}
                        </Row>
                    </Container>
                </BasicSection.BodyElevated>
            </BasicSection>
        </Layout>
    );
};

export default FeaturesPage;

interface FeatureProps {
    titleKey: string;
    descriptionKey: string;
    components?: Record<string | number, React.ReactNode>;
    slug: string;
}

const FeatureCard: React.FC<FeatureProps> = ({
    titleKey,
    descriptionKey,
    components,
    slug,
}) => {
    const [isHovering, setIsHovering] = useState(false);

    return (
        <Col
            className="my-3"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
        >
            <div
                className={
                    "card feature-card" +
                    (isHovering ? " feature-card-hovered" : "")
                }
                style={{ height: "100%" }}
            >
                <CardContents className="card-body mx-3 my-2 my-md-3">
                    <h5
                        className={
                            "card-title mt-1" + (isHovering ? " text-bold" : "")
                        }
                    >
                        <Trans i18nKey={titleKey} />
                    </h5>
                    <div
                        className={
                            "card-text " +
                            (isHovering ? "" : "text-muted ") +
                            "mt-md-3"
                        }
                    >
                        <Trans
                            i18nKey={descriptionKey}
                            components={components}
                        />
                    </div>
                </CardContents>
                <CardIcon {...{ slug, isFocussed: isHovering }} />
            </div>
        </Col>
    );
};

const CardContents = styled.div`
    z-index: 1;
`;

const CardIconContainer = styled.div`
    position: absolute;
    bottom: 1rem;
    right: 1rem;
    opacity: 17%;

    width: 24px;
    height: 24px;

    @media (min-width: 576px) {
        width: 36px;
        height: 36px;
    }
`;

const CardIcon: React.FC<{ slug: string; isFocussed: boolean }> = ({
    slug,
}) => {
    const icon = getIcon(slug);
    return <CardIconContainer className="d-flex">{icon}</CardIconContainer>;
};

const getIcon = (slug: string) => {
    switch (slug) {
        case "lock":
            return <LockClosedIcon />;
        case "shield":
            return <ShieldCheckIcon />;
        case "open":
            return <BookOpenIcon />;
        case "upload":
            return <CloudArrowUpIcon />;
        case "share":
            return <UserGroupIcon />;
        case "link":
            return <LinkIcon />;
        case "collaborate":
            return <ArrowsPointingInIcon />;
        case "money":
            return <CurrencyDollarIcon />;
        case "import":
            return <ArrowsUpDownIcon />;
        case "export":
            return <FolderArrowDownIcon />;
        case "hidden":
            return <EyeSlashIcon />;
        case "description":
            return <ChatBubbleBottomCenterIcon />;
        case "search":
            return <MagnifyingGlassIcon />;
        case "support":
            return <QuestionMarkCircleIcon />;
        case "community":
            return <HeartIcon />;
        case "more":
            return <EllipsisHorizontalCircleIcon />;
        case "theme":
            return <SunIcon />;
        case "2fa":
            return <FingerPrintIcon />;
        case "memories":
            return <CakeIcon />;
        case "free":
            return <BoltIcon />;
    }
};
